import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Activity, Plus, Trash2, Pencil, Calendar, TrendingUp, FileDown, AlertTriangle, Bug, GitCompare } from 'lucide-react';
import { toast } from 'sonner';
import { exportSymptomsToPDF } from '../utils/pdfExport';
import TimelineButton from '../components/TimelineButton';
import { AttachmentSection } from '../components/AttachmentSection';
import PhotoCompareDialog from '../components/PhotoCompareDialog';
import { todayLocal, formatLocalDate } from '../utils/dateUtils';

export const SymptomTracker = () => {
  const { API, token } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [photosOpenFor, setPhotosOpenFor] = useState({});
  const [compareOpen, setCompareOpen] = useState(false);
  const [formData, setFormData] = useState({
    child_name: '',
    date: todayLocal(),
    symptoms: [],
    severity: 5,
    triggers: [],
    notes: '',
    // New strep tracking fields
    strep_event: 'none', // 'none', 'exposure', 'positive_test', 'suspected', 'recovered'
    strep_test_type: '', // 'rapid', 'culture', 'both', 'none'
    strep_test_result: '', // 'positive', 'negative', 'pending'
    strep_exposure_source: '',
    ocd_anxiety_severity: 5,
    behavioral_severity: 5,
    sleep_quality: 5
  });
  const [symptomInput, setSymptomInput] = useState('');
  const [triggerInput, setTriggerInput] = useState('');

  const commonSymptoms = ['OCD', 'Anxiety', 'Tics', 'Irritability', 'Sleep Issues', 'Aggression', 'Mood Swings', 'Regression', 'Baby Talk'];
  const commonTriggers = ['Illness', 'Stress', 'Lack of Sleep', 'Diet Change', 'Weather', 'Allergen Exposure', 'Strep Exposure'];
  
  const strepEventTypes = [
    { value: 'none', label: 'No Strep Event', color: 'bg-stone-100 text-stone-600' },
    { value: 'exposure', label: 'Strep Exposure', color: 'bg-yellow-100 text-yellow-700' },
    { value: 'suspected', label: 'Suspected Infection', color: 'bg-orange-100 text-orange-700' },
    { value: 'positive_test', label: 'Positive Test', color: 'bg-red-100 text-red-700' },
    { value: 'recovered', label: 'Recovered from Strep', color: 'bg-green-100 text-green-700' }
  ];

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/symptoms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(response.data);
    } catch (error) {
      console.error('Error fetching entries:', error);
      toast.error('Failed to load symptom entries');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEntry) {
        await axios.put(`${API}/symptoms/${editingEntry.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Symptom entry updated');
      } else {
        await axios.post(`${API}/symptoms`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Symptom entry added successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save entry');
    }
  };

  const startEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      child_name: entry.child_name || '',
      date: entry.date,
      symptoms: entry.symptoms || [],
      severity: entry.severity ?? 5,
      triggers: entry.triggers || [],
      notes: entry.notes || '',
      strep_event: entry.strep_event || 'none',
      strep_test_type: entry.strep_test_type || '',
      strep_test_result: entry.strep_test_result || '',
      strep_exposure_source: entry.strep_exposure_source || '',
      ocd_anxiety_severity: entry.ocd_anxiety_severity ?? 5,
      behavioral_severity: entry.behavioral_severity ?? 5,
      sleep_quality: entry.sleep_quality ?? 5
    });
    setDialogOpen(true);
  };

  const deleteEntry = async (id) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    try {
      await axios.delete(`${API}/symptoms/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Entry deleted');
      fetchEntries();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const resetForm = () => {
    setFormData({
      child_name: '',
      date: todayLocal(),
      symptoms: [],
      severity: 5,
      triggers: [],
      notes: '',
      strep_event: 'none',
      strep_test_type: '',
      strep_test_result: '',
      strep_exposure_source: '',
      ocd_anxiety_severity: 5,
      behavioral_severity: 5,
      sleep_quality: 5
    });
    setEditingEntry(null);
    setSymptomInput('');
    setTriggerInput('');
  };

  const addSymptom = (symptom) => {
    if (symptom && !formData.symptoms.includes(symptom)) {
      setFormData({ ...formData, symptoms: [...formData.symptoms, symptom] });
      setSymptomInput('');
    }
  };

  const removeSymptom = (symptom) => {
    setFormData({ ...formData, symptoms: formData.symptoms.filter(s => s !== symptom) });
  };

  const addTrigger = (trigger) => {
    if (trigger && !formData.triggers.includes(trigger)) {
      setFormData({ ...formData, triggers: [...formData.triggers, trigger] });
      setTriggerInput('');
    }
  };

  const removeTrigger = (trigger) => {
    setFormData({ ...formData, triggers: formData.triggers.filter(t => t !== trigger) });
  };

  return (
    <div className="space-y-8 overflow-x-hidden" data-testid="symptom-tracker">
      {/* Header */}
      <div className="bg-gradient-to-br from-accent/10 to-secondary/20 rounded-xl p-4 sm:p-8 md:p-12">
        <div className="flex flex-col gap-4">
          <div>
            <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
              <Activity className="w-8 h-8 sm:w-12 sm:h-12 text-accent flex-shrink-0" strokeWidth={1.5} />
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-nunito font-bold text-[#2D2D2D]">
                Symptom Tracker
              </h1>
            </div>
            <p className="text-base sm:text-lg text-[#57534E]">
              Log and track symptoms to identify patterns and triggers.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-3 items-center">
            <TimelineButton />
            <Button
              variant="outline"
              onClick={() => exportSymptomsToPDF(entries)}
              disabled={entries.length === 0}
              className="rounded-full px-3 sm:px-5 text-sm sm:text-base"
              data-testid="export-symptoms-pdf"
            >
              <FileDown className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Export PDF</span>
              <span className="sm:hidden">PDF</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => setCompareOpen(true)}
              className="rounded-full px-3 sm:px-5 text-sm sm:text-base"
              data-testid="compare-photos-btn"
            >
              <GitCompare className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Compare Photos</span>
              <span className="sm:hidden">Compare</span>
            </Button>
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button
                  className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-4 sm:px-6 py-4 sm:py-6 font-bold shadow-sm transition-transform hover:scale-[1.02] text-sm sm:text-base"
                  data-testid="add-entry-button"
                >
                  <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">New Entry</span>
                  <span className="sm:hidden">Add</span>
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl font-nunito font-bold">{editingEntry ? 'Edit Symptom Entry' : 'Log Symptom Entry'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="child_name">Child's Name</Label>
                  <Input
                    id="child_name"
                    value={formData.child_name}
                    onChange={(e) => setFormData({ ...formData, child_name: e.target.value })}
                    required
                    data-testid="child-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="date">Date</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    data-testid="date-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Symptoms</Label>
                  <div className="flex gap-2">
                    <Input
                      value={symptomInput}
                      onChange={(e) => setSymptomInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSymptom(symptomInput))}
                      placeholder="Type and press Enter"
                      data-testid="symptom-input"
                    />
                    <Button
                      type="button"
                      onClick={() => addSymptom(symptomInput)}
                      className="bg-primary text-white"
                    >
                      Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {commonSymptoms.map(s => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => addSymptom(s)}
                        className="px-3 py-1 bg-stone-100 hover:bg-primary/20 text-sm rounded-full"
                      >
                        + {s}
                      </button>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {formData.symptoms.map(s => (
                      <span key={s} className="px-3 py-1 bg-accent/20 text-accent rounded-full text-sm flex items-center gap-2">
                        {s}
                        <button type="button" onClick={() => removeSymptom(s)}>×</button>
                      </span>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="severity">Severity (1-10)</Label>
                  <div className="flex items-center gap-4">
                    <input
                      id="severity"
                      type="range"
                      min="1"
                      max="10"
                      value={formData.severity}
                      onChange={(e) => setFormData({ ...formData, severity: parseInt(e.target.value) })}
                      className="flex-1"
                      data-testid="severity-slider"
                    />
                    <span className="text-2xl font-bold text-accent w-12 text-center">{formData.severity}</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Triggers</Label>
                  <div className="flex gap-2">
                    <Input
                      value={triggerInput}
                      onChange={(e) => setTriggerInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTrigger(triggerInput))}
                      placeholder="Type and press Enter"
                      data-testid="trigger-input"
                    />
                    <Button
                      type="button"
                      onClick={() => addTrigger(triggerInput)}
                      className="bg-primary text-white"
                    >
                      Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {commonTriggers.map(t => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => addTrigger(t)}
                        className="px-3 py-1 bg-stone-100 hover:bg-primary/20 text-sm rounded-full"
                      >
                        + {t}
                      </button>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {formData.triggers.map(t => (
                      <span key={t} className="px-3 py-1 bg-primary/20 text-primary rounded-full text-sm flex items-center gap-2">
                        {t}
                        <button type="button" onClick={() => removeTrigger(t)}>×</button>
                      </span>
                    ))}
                  </div>
                </div>

                {/* Strep Exposure/Infection Section */}
                <div className="space-y-4 p-4 bg-red-50/50 rounded-xl border border-red-100">
                  <div className="flex items-center gap-2">
                    <Bug className="w-5 h-5 text-red-600" />
                    <Label className="text-base font-semibold text-red-800">Strep Exposure / Infection Tracking</Label>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm text-red-700">Strep Event Type</Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {strepEventTypes.map(type => (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => setFormData({ ...formData, strep_event: type.value })}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-all border-2 ${
                            formData.strep_event === type.value
                              ? `${type.color} border-current`
                              : 'bg-white text-stone-500 border-stone-200 hover:border-stone-300'
                          }`}
                          data-testid={`strep-event-${type.value}`}
                        >
                          {type.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {(formData.strep_event === 'positive_test' || formData.strep_event === 'suspected') && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-sm text-red-700">Test Type</Label>
                          <select
                            value={formData.strep_test_type}
                            onChange={(e) => setFormData({ ...formData, strep_test_type: e.target.value })}
                            className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm"
                          >
                            <option value="">Select test type</option>
                            <option value="rapid">Rapid Strep Test</option>
                            <option value="culture">Throat Culture</option>
                            <option value="both">Both Tests</option>
                            <option value="none">No Test Yet</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm text-red-700">Test Result</Label>
                          <select
                            value={formData.strep_test_result}
                            onChange={(e) => setFormData({ ...formData, strep_test_result: e.target.value })}
                            className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm"
                          >
                            <option value="">Select result</option>
                            <option value="positive">Positive</option>
                            <option value="negative">Negative</option>
                            <option value="pending">Pending</option>
                          </select>
                        </div>
                      </div>
                    </>
                  )}

                  {formData.strep_event === 'exposure' && (
                    <div className="space-y-2">
                      <Label className="text-sm text-red-700">Exposure Source</Label>
                      <Input
                        value={formData.strep_exposure_source}
                        onChange={(e) => setFormData({ ...formData, strep_exposure_source: e.target.value })}
                        placeholder="e.g., School classmate, sibling, playdate"
                        className="bg-white"
                      />
                    </div>
                  )}
                </div>

                {/* Additional Severity Scores */}
                <div className="space-y-4 p-4 bg-blue-50/50 rounded-xl border border-blue-100">
                  <Label className="text-base font-semibold text-blue-800">Detailed Symptom Scores</Label>
                  
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label className="text-sm text-blue-700">OCD/Anxiety Severity (1-10)</Label>
                      <div className="flex items-center gap-4">
                        <input
                          type="range"
                          min="1"
                          max="10"
                          value={formData.ocd_anxiety_severity}
                          onChange={(e) => setFormData({ ...formData, ocd_anxiety_severity: parseInt(e.target.value) })}
                          className="flex-1"
                        />
                        <span className="text-xl font-bold text-blue-600 w-10 text-center">{formData.ocd_anxiety_severity}</span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm text-blue-700">Behavioral Severity (1-10)</Label>
                      <div className="flex items-center gap-4">
                        <input
                          type="range"
                          min="1"
                          max="10"
                          value={formData.behavioral_severity}
                          onChange={(e) => setFormData({ ...formData, behavioral_severity: parseInt(e.target.value) })}
                          className="flex-1"
                        />
                        <span className="text-xl font-bold text-blue-600 w-10 text-center">{formData.behavioral_severity}</span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm text-blue-700">Sleep Quality (1-10, 10 = best)</Label>
                      <div className="flex items-center gap-4">
                        <input
                          type="range"
                          min="1"
                          max="10"
                          value={formData.sleep_quality}
                          onChange={(e) => setFormData({ ...formData, sleep_quality: parseInt(e.target.value) })}
                          className="flex-1"
                        />
                        <span className="text-xl font-bold text-blue-600 w-10 text-center">{formData.sleep_quality}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={4}
                    placeholder="Additional observations..."
                    data-testid="notes-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-accent text-accent-foreground hover:bg-accent/90 rounded-full h-12 font-bold"
                  data-testid="save-entry-button"
                >
                  {editingEntry ? 'Update Entry' : 'Save Entry'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>
      </div>

      {/* Entries List */}
      {loading ? (
        <div className="text-center py-12">
          <p className="text-[#A8A29E]">Loading entries...</p>
        </div>
      ) : entries.length === 0 ? (
        <Card className="bg-white rounded-xl border border-stone-100 p-12 text-center">
          <Activity className="w-16 h-16 text-[#A8A29E] mx-auto mb-4" />
          <p className="text-[#57534E] text-lg mb-4">No symptom entries yet</p>
          <p className="text-[#A8A29E] mb-6">Start tracking to identify patterns and triggers</p>
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-6 py-3"
          >
            Add First Entry
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {entries.map((entry) => (
            <Card key={entry.id} className="bg-white rounded-xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-4 sm:p-6 hover:shadow-md transition-shadow overflow-hidden">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="flex-1 space-y-3 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                    <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0" />
                    <h3 className="font-nunito font-bold text-base sm:text-lg text-[#2D2D2D]">{entry.child_name}</h3>
                    <span className="text-sm text-[#A8A29E]">{formatLocalDate(entry.date)}</span>
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm text-[#A8A29E]">Symptoms:</p>
                    <div className="flex flex-wrap gap-1.5 sm:gap-2">
                      {entry.symptoms.map((symptom, i) => (
                        <span key={i} className="px-2 sm:px-3 py-1 bg-accent/10 text-accent rounded-full text-xs sm:text-sm">
                          {symptom}
                        </span>
                      ))}
                    </div>
                  </div>

                  {entry.triggers.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm text-[#A8A29E]">Triggers:</p>
                      <div className="flex flex-wrap gap-1.5 sm:gap-2">
                        {entry.triggers.map((trigger, i) => (
                          <span key={i} className="px-2 sm:px-3 py-1 bg-primary/10 text-primary rounded-full text-xs sm:text-sm">
                            {trigger}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {entry.strep_event && entry.strep_event !== 'none' && (
                    <div className="flex items-center gap-2 p-2 bg-red-50 rounded-lg border border-red-200">
                      <Bug className="w-4 h-4 text-red-600 flex-shrink-0" />
                      <span className="text-xs sm:text-sm font-medium text-red-700">
                        {entry.strep_event === 'exposure' && `Strep Exposure${entry.strep_exposure_source ? `: ${entry.strep_exposure_source}` : ''}`}
                        {entry.strep_event === 'suspected' && 'Suspected Strep Infection'}
                        {entry.strep_event === 'positive_test' && `Positive Strep Test${entry.strep_test_type ? ` (${entry.strep_test_type})` : ''}`}
                        {entry.strep_event === 'recovered' && 'Recovered from Strep'}
                      </span>
                    </div>
                  )}

                  {entry.notes && (
                    <p className="text-[#57534E] text-xs sm:text-sm italic break-words">{entry.notes}</p>
                  )}

                  <div className="pt-3 mt-2 border-t border-stone-100">
                    {photosOpenFor[entry.id] ? (
                      <AttachmentSection
                        parentType="symptom"
                        parentId={entry.id}
                        token={token}
                        title="Photos"
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => setPhotosOpenFor(prev => ({ ...prev, [entry.id]: true }))}
                        className="text-xs text-primary hover:underline flex items-center gap-1"
                        data-testid={`open-photos-${entry.id}`}
                      >
                        <Plus className="w-3 h-3" /> Add / view photos
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3 sm:ml-4 pt-2 sm:pt-0 border-t sm:border-t-0 border-stone-100">
                  <div className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-bold text-sm sm:text-base ${
                    entry.severity >= 7 ? 'bg-accent/20 text-accent' :
                    entry.severity >= 4 ? 'bg-secondary text-secondary-foreground' :
                    'bg-primary/10 text-primary'
                  }`}>
                    <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />
                    {entry.severity}/10
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => startEdit(entry)}
                    className="text-primary hover:text-primary/80"
                    data-testid={`edit-entry-${entry.id}`}
                  >
                    <Pencil className="w-4 h-4 sm:w-5 sm:h-5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteEntry(entry.id)}
                    className="text-accent hover:text-accent/80"
                    data-testid={`delete-entry-${entry.id}`}
                  >
                    <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <PhotoCompareDialog
        open={compareOpen}
        onOpenChange={setCompareOpen}
        token={token}
        parentType="symptom"
        title="Compare Symptom Photos"
        description="Pick two symptom photos to see them side by side. Photos are sorted oldest → newest by entry date."
        filterOptions={Array.from(new Set(entries.map(e => e.child_name).filter(Boolean)))}
        filterLabel="All children"
      />
    </div>
  );
};

export default SymptomTracker;
